import { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search,
  SearchX,
  SlidersHorizontal,
  X,
  ChevronDown,
  ChevronUp,
  Car,
  Calendar,
  Gauge,
  Fuel,
  Settings2,
  DollarSign,
  RotateCcw,
  ArrowLeft,
  Phone,
  Mail,
  MapPin,
  Facebook,
  Instagram
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';

// Vehicle data will be fetched dynamically

const FilterSection = ({ title, icon: Icon, children, defaultOpen = true }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="border-b border-white/10 pb-4 mb-4 last:border-0 last:pb-0 last:mb-0">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between w-full text-left mb-3 group"
      >
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-blue-400" />
          <span className="font-medium text-white text-sm">{title}</span>
        </div>
        {isOpen ? (
          <ChevronUp className="w-4 h-4 text-white/50 group-hover:text-white transition-colors" />
        ) : (
          <ChevronDown className="w-4 h-4 text-white/50 group-hover:text-white transition-colors" />
        )}
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const Vehiculos = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [vehiclesData, setVehiclesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [marcas, setMarcas] = useState([]);
  const [combustibles, setCombustibles] = useState([]);
  const [transmisiones, setTransmisiones] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('relevancia');
  const [filters, setFilters] = useState({
    marcas: [],
    combustibles: [],
    transmisiones: [],
    precioMin: 0,
    precioMax: 100000,
    anioMin: 2015,
    anioMax: 2025,
    kmMax: 200000,
  });

  const calculateActiveFilters = () => {
    let count = 0;
    if (filters.marcas.length > 0) count += filters.marcas.length;
    if (filters.combustibles.length > 0) count += filters.combustibles.length;
    if (filters.transmisiones.length > 0) count += filters.transmisiones.length;
    if (filters.precioMin > 0 || filters.precioMax < 100000) count++;
    if (filters.anioMin > 2015 || filters.anioMax < 2025) count++;
    if (filters.kmMax < 200000) count++;
    return count;
  };

  const toggleFilter = (category, value) => {
    setFilters(prev => {
      const current = prev[category];
      const updated = current.includes(value)
        ? current.filter(v => v !== value)
        : [...current, value];
      return { ...prev, [category]: updated };
    });
  };

  const clearFilters = () => {
    setFilters({
      marcas: [],
      combustibles: [],
      transmisiones: [],
      precioMin: 0,
      precioMax: 100000,
      anioMin: 2015,
      anioMax: 2025,
      kmMax: 200000,
    });
  };

  useEffect(() => {
    const fetchVehicles = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/multiavisos/vehiculos`);
        const data = response.data.Vehicles || [];

        // Map the data
        const mappedData = data.map(v => ({
          id: v.VehicleId,
          marca: v.Brand,
          modelo: v.Model,
          version: v.Version,
          anio: v.Year,
          km: v.Mileage?.Value || 0,
          precio: v.Price?.Value || 0,
          combustible: v.Fuel,
          transmision: v.Transmission,
          imagen: (v.Images && v.Images.length > 0)
            ? `${process.env.REACT_APP_BACKEND_URL}/api/multiavisos/images?url=${encodeURIComponent(v.Images[0])}`
            : 'https://images.unsplash.com/photo-1623869675781-80aa31012a5a?w=400&h=300&fit=crop',
          destacado: v.Featured === "true" || v.Featured === true
        }));

        setVehiclesData(mappedData);

        const uniqueMarcas = [...new Set(mappedData.map(v => v.marca))].filter(Boolean);
        const uniqueCombustibles = [...new Set(mappedData.map(v => v.combustible))].filter(Boolean);
        const uniqueTransmisiones = [...new Set(mappedData.map(v => v.transmision))].filter(Boolean);

        setMarcas(uniqueMarcas);
        setCombustibles(uniqueCombustibles);
        setTransmisiones(uniqueTransmisiones);
      } catch (error) {
        console.error('Error fetching vehicles:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchVehicles();
  }, []);

  const filteredVehicles = vehiclesData.filter(v => {
    if (searchTerm && !`${v.marca} ${v.modelo}`.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    if (filters.marcas.length > 0 && !filters.marcas.includes(v.marca)) return false;
    if (filters.combustibles.length > 0 && !filters.combustibles.includes(v.combustible)) return false;
    if (filters.transmisiones.length > 0 && !filters.transmisiones.includes(v.transmision)) return false;
    if (v.precio < filters.precioMin || v.precio > filters.precioMax) return false;
    if (v.anio < filters.anioMin || v.anio > filters.anioMax) return false;
    if (v.km > filters.kmMax) return false;
    return true;
  });

  const sortedVehicles = [...filteredVehicles].sort((a, b) => {
    switch (sortBy) {
      case 'precio-asc': return a.precio - b.precio;
      case 'precio-desc': return b.precio - a.precio;
      case 'anio-desc': return b.anio - a.anio;
      case 'anio-asc': return a.anio - b.anio;
      case 'km-asc': return a.km - b.km;
      default: return 0;
    }
  });

  const destacados = vehiclesData.filter(v => v.destacado);

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-xl">Cargando vehículos...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2 text-white hover:text-white/80 transition-colors">
              <ArrowLeft className="w-5 h-5" />
              <span className="font-semibold">Amaya Motors</span>
            </Link>
            <h1 className="text-lg font-bold">Vehículos</h1>
            <div className="w-24"></div>
          </div>
        </div>
      </header>

      <main className="pt-20 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Destacados - Premium Showcase */}
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-8">
              <div className="relative">
                <div className="w-2 h-8 bg-gradient-to-b from-amber-400 to-amber-600 rounded-full" />
                <div className="absolute inset-0 w-2 h-8 bg-amber-400 rounded-full blur-md opacity-50" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">Selección Premium</h2>
                <p className="text-white/50 text-sm">Los mejores vehículos seleccionados para vos</p>
              </div>
            </div>

            {/* Featured Carousel/Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {destacados.map((vehicle, index) => (
                <motion.div
                  key={vehicle.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="group cursor-pointer"
                >
                  <div className="block relative rounded-2xl overflow-hidden bg-gradient-to-br from-white/10 to-white/5 border border-white/10 hover:border-amber-500/50 transition-all duration-500 hover:shadow-[0_0_40px_rgba(251,191,36,0.15)]">
                    {/* Stretched link: makes the whole card tappable without nesting an <a> inside another <a> */}
                    <Link
                      to={`/vehiculos/${vehicle.id}`}
                      className="absolute inset-0 z-10"
                      aria-label={`Ver ${vehicle.marca} ${vehicle.modelo}`}
                    />

                    {/* Glow effect on hover */}
                    <div className="absolute inset-0 bg-gradient-to-br from-amber-500/0 to-amber-500/0 group-hover:from-amber-500/5 group-hover:to-transparent transition-all duration-500 pointer-events-none" />

                    <div className="flex flex-col sm:flex-row pointer-events-none">
                      {/* Image Section */}
                      <div className="relative sm:w-1/2 h-48 sm:h-56 overflow-hidden">
                        <img
                          src={vehicle.imagen}
                          alt={`${vehicle.marca} ${vehicle.modelo}`}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                        />
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-black/80 hidden sm:block" />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent sm:hidden" />

                        {/* Premium Badge */}
                        <div className="absolute top-3 left-3">
                          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-gradient-to-r from-amber-500 to-amber-600 shadow-lg shadow-amber-500/30">
                            <svg className="w-3.5 h-3.5 text-black" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                            </svg>
                            <span className="text-xs font-bold text-black uppercase tracking-wide">Premium</span>
                          </div>
                        </div>

                        {/* Year Badge */}
                        <div className="absolute top-3 right-3 px-2.5 py-1 rounded-lg bg-black/60 backdrop-blur-md border border-white/10">
                          <span className="text-white font-bold text-sm">{vehicle.anio}</span>
                        </div>
                      </div>

                      {/* Content Section */}
                      <div className="sm:w-1/2 p-5 flex flex-col justify-between relative z-20">
                        <div>
                          {/* Title */}
                          <div className="mb-3">
                            <h3 className="text-xl font-bold text-white group-hover:text-amber-400 transition-colors">
                              {vehicle.marca} {vehicle.modelo}
                            </h3>
                            <p className="text-white/60 text-sm">{vehicle.version}</p>
                          </div>

                          {/* Specs Grid */}
                          <div className="grid grid-cols-2 gap-3 mb-4">
                            <div className="flex items-center gap-2 text-white/70">
                              <Gauge className="w-4 h-4 text-amber-400/70" />
                              <span className="text-sm">{(vehicle.km / 1000).toFixed(0)}k km</span>
                            </div>
                            <div className="flex items-center gap-2 text-white/70">
                              <Fuel className="w-4 h-4 text-amber-400/70" />
                              <span className="text-sm">{vehicle.combustible}</span>
                            </div>
                            <div className="flex items-center gap-2 text-white/70">
                              <Settings2 className="w-4 h-4 text-amber-400/70" />
                              <span className="text-sm">{vehicle.transmision}</span>
                            </div>
                            <div className="flex items-center gap-2 text-white/70">
                              <Calendar className="w-4 h-4 text-amber-400/70" />
                              <span className="text-sm">{vehicle.anio}</span>
                            </div>
                          </div>
                        </div>

                        {/* Price & CTA */}
                        <div className="flex items-end justify-between pt-3 border-t border-white/10">
                          <div>
                            <span className="text-white/40 text-xs uppercase tracking-wider">Precio</span>
                            <div className="text-2xl font-black text-white">
                              <span className="text-amber-400">$</span>{vehicle.precio.toLocaleString()}
                            </div>
                          </div>
                          <Link to={`/vehiculos/${vehicle.id}`} className="relative z-20 pointer-events-auto px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-black font-semibold text-sm transition-all hover:shadow-lg hover:shadow-amber-500/30">
                            Ver más
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </section>

          {/* Search & Filters Bar */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
              <input
                type="text"
                placeholder="Buscar por marca o modelo..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 rounded-xl border border-white/10 bg-white/5 text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all"
              />
            </div>
            <Button
              onClick={() => setSidebarOpen(true)}
              className="flex items-center gap-2 px-6 py-3 h-auto rounded-xl bg-white/10 hover:bg-white/20 text-white border border-white/10"
            >
              <SlidersHorizontal className="w-5 h-5" />
              <span>Filtros</span>
              {calculateActiveFilters() > 0 && (
                <Badge className="bg-blue-500 text-white ml-1">{calculateActiveFilters()}</Badge>
              )}
            </Button>
          </div>

          {/* Active Filters Tags */}
          {calculateActiveFilters() > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {filters.marcas.map(marca => (
                <Badge
                  key={marca}
                  className="bg-blue-500/20 text-blue-300 cursor-pointer hover:opacity-80"
                  onClick={() => toggleFilter('marcas', marca)}
                >
                  {marca} <X className="w-3 h-3 ml-1" />
                </Badge>
              ))}
              {filters.combustibles.map(c => (
                <Badge
                  key={c}
                  className="bg-green-500/20 text-green-300 cursor-pointer hover:opacity-80"
                  onClick={() => toggleFilter('combustibles', c)}
                >
                  {c} <X className="w-3 h-3 ml-1" />
                </Badge>
              ))}
              {filters.transmisiones.map(t => (
                <Badge
                  key={t}
                  className="bg-purple-500/20 text-purple-300 cursor-pointer hover:opacity-80"
                  onClick={() => toggleFilter('transmisiones', t)}
                >
                  {t} <X className="w-3 h-3 ml-1" />
                </Badge>
              ))}
              {(filters.precioMin > 0 || filters.precioMax < 100000) && (
                <Badge
                  className="bg-amber-500/20 text-amber-300 cursor-pointer hover:opacity-80"
                  onClick={() => setFilters(prev => ({ ...prev, precioMin: 0, precioMax: 100000 }))}
                >
                  ${filters.precioMin.toLocaleString()} - ${filters.precioMax.toLocaleString()} <X className="w-3 h-3 ml-1" />
                </Badge>
              )}
              {(filters.anioMin > 2015 || filters.anioMax < 2025) && (
                <Badge
                  className="bg-amber-500/20 text-amber-300 cursor-pointer hover:opacity-80"
                  onClick={() => setFilters(prev => ({ ...prev, anioMin: 2015, anioMax: 2025 }))}
                >
                  {filters.anioMin} - {filters.anioMax} <X className="w-3 h-3 ml-1" />
                </Badge>
              )}
              {filters.kmMax < 200000 && (
                <Badge
                  className="bg-amber-500/20 text-amber-300 cursor-pointer hover:opacity-80"
                  onClick={() => setFilters(prev => ({ ...prev, kmMax: 200000 }))}
                >
                  Hasta {filters.kmMax.toLocaleString()} km <X className="w-3 h-3 ml-1" />
                </Badge>
              )}
              <button
                onClick={clearFilters}
                className="text-sm text-white/60 hover:text-white flex items-center gap-1"
              >
                <RotateCcw className="w-3 h-3" /> Limpiar
              </button>
            </div>
          )}

          {/* Results count & Sort */}
          <div className="flex items-center justify-between gap-3 mb-4">
            <p className="text-white/50 text-sm">{filteredVehicles.length} vehículos encontrados</p>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="bg-white/5 border border-white/10 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
            >
              <option value="relevancia" className="bg-gray-900">Relevancia</option>
              <option value="precio-asc" className="bg-gray-900">Precio: menor a mayor</option>
              <option value="precio-desc" className="bg-gray-900">Precio: mayor a menor</option>
              <option value="anio-desc" className="bg-gray-900">Año: más nuevo</option>
              <option value="anio-asc" className="bg-gray-900">Año: más antiguo</option>
              <option value="km-asc" className="bg-gray-900">Menor kilometraje</option>
            </select>
          </div>

          {/* Empty state */}
          {filteredVehicles.length === 0 ? (
            <div className="flex flex-col items-center justify-center text-center py-16 px-4 rounded-2xl bg-white/5 border border-white/10">
              <SearchX className="w-12 h-12 text-white/30 mb-4" />
              <h3 className="text-lg font-semibold text-white mb-1">No encontramos vehículos</h3>
              <p className="text-white/50 text-sm mb-6 max-w-sm">
                Probá ajustar o limpiar los filtros para ver más resultados.
              </p>
              <Button onClick={clearFilters} className="bg-blue-500 hover:bg-blue-600 text-white">
                <RotateCcw className="w-4 h-4 mr-2" /> Limpiar filtros
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              {sortedVehicles.map((vehicle, index) => (
                <motion.div
                  key={vehicle.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.03 }}
                  className="group cursor-pointer"
                >
                  {/* Whole card is one tap target (no nested links) so mobile users don't have to aim for a tiny "Ver más" */}
                  <Link to={`/vehiculos/${vehicle.id}`} className="block rounded-xl overflow-hidden bg-white/5 border border-white/10 hover:border-white/30 transition-all">
                    <div className="relative h-32 overflow-hidden">
                      <img
                        src={vehicle.imagen}
                        alt={`${vehicle.marca} ${vehicle.modelo}`}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                      <div className="absolute bottom-2 left-2 right-2">
                        <h3 className="text-white text-sm font-bold truncate">{vehicle.marca} {vehicle.modelo}</h3>
                        <p className="text-white/60 text-xs truncate">{vehicle.version}</p>
                      </div>
                      <Badge className="absolute top-2 right-2 bg-blue-500/90 text-white text-xs px-1.5 py-0.5">
                        {vehicle.anio}
                      </Badge>
                    </div>
                    <div className="p-3">
                      <div className="flex items-center justify-between text-xs text-white/50 mb-2">
                        <span className="flex items-center gap-1">
                          <Gauge className="w-3 h-3" />
                          {(vehicle.km / 1000).toFixed(0)}k
                        </span>
                        <span>{vehicle.combustible}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-base font-bold text-blue-400">${vehicle.precio.toLocaleString()}</span>
                        <span className="text-xs bg-white/10 group-hover:bg-white/20 text-white px-2 py-1 rounded">Ver más</span>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-black border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* Logo & Description */}
            <div className="md:col-span-2">
              <h3 className="text-xl font-bold mb-4">Amaya Motors</h3>
              <p className="text-white/60 text-sm mb-6 max-w-sm">
                Concesionario oficial de las mejores marcas. Venta de vehículos nuevos y usados con financiación a medida.
              </p>
              <div className="flex gap-3">
                <a href="https://www.facebook.com/profile.php?id=61584072839314#" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors">
                  <Facebook className="w-5 h-5" />
                </a>
                <a href="https://www.instagram.com/amaya.nuevocentro/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors">
                  <Instagram className="w-5 h-5" />
                </a>
              </div>
            </div>

            {/* Enlaces */}
            <div>
              <h4 className="font-semibold mb-4">Enlaces</h4>
              <ul className="space-y-2 text-sm text-white/60">
                <li><Link to="/" className="hover:text-white transition-colors">Inicio</Link></li>
                <li><Link to="/vehiculos" className="hover:text-white transition-colors">Vehículos</Link></li>
                <li><a href="#" className="hover:text-white transition-colors">Sucursales</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Contacto</a></li>
              </ul>
            </div>

            {/* Contacto */}
            <div>
              <h4 className="font-semibold mb-4">Contacto</h4>
              <ul className="space-y-3 text-sm text-white/60">
                <li className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  <span>info@amayamotors.com.uy</span>
                </li>
                <li className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  <span>+598 99 123 456</span>
                </li>
                <li className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  <span>Montevideo, Uruguay</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-white/10 mt-8 pt-8 text-center text-white/40 text-sm">
            <p>© 2024 Amaya Motors. Todos los derechos reservados.</p>
          </div>
        </div>
      </footer>

      {/* Filter Sidebar */}
      <AnimatePresence>
        {sidebarOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSidebarOpen(false)}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
            />

            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed right-0 top-0 h-full w-full max-w-sm bg-gray-900 z-50 overflow-y-auto"
            >
              <div className="sticky top-0 bg-gray-900 border-b border-white/10 p-4 flex items-center justify-between z-10">
                <div>
                  <h3 className="text-lg font-bold text-white">Filtros</h3>
                  <p className="text-xs text-white/50">{calculateActiveFilters()} activos</p>
                </div>
                <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(false)} className="text-white/60 hover:text-white">
                  <X className="w-5 h-5" />
                </Button>
              </div>

              <div className="p-4 space-y-4">
                <FilterSection title="Marca" icon={Car}>
                  <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto pr-2">
                    {marcas.map(marca => (
                      <label key={marca} className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-all text-sm ${filters.marcas.includes(marca) ? 'bg-blue-500/20 text-blue-300' : 'bg-white/5 text-white/70 hover:bg-white/10'}`}>
                        <Checkbox checked={filters.marcas.includes(marca)} onCheckedChange={() => toggleFilter('marcas', marca)} className="border-white/30 data-[state=checked]:bg-blue-500" />
                        {marca}
                      </label>
                    ))}
                  </div>
                </FilterSection>

                <FilterSection title="Precio (USD)" icon={DollarSign}>
                  <div className="space-y-4 px-1">
                    <div className="flex justify-between text-xs text-white/70">
                      <span>${filters.precioMin.toLocaleString()}</span>
                      <span>${filters.precioMax.toLocaleString()}</span>
                    </div>
                    <Slider value={[filters.precioMin, filters.precioMax]} min={0} max={100000} step={1000} onValueChange={([min, max]) => setFilters(prev => ({ ...prev, precioMin: min, precioMax: max }))} className="py-2" />
                  </div>
                </FilterSection>

                <FilterSection title="Año" icon={Calendar}>
                  <div className="space-y-4 px-1">
                    <div className="flex justify-between text-xs text-white/70">
                      <span>{filters.anioMin}</span>
                      <span>{filters.anioMax}</span>
                    </div>
                    <Slider value={[filters.anioMin, filters.anioMax]} min={2000} max={2025} step={1} onValueChange={([min, max]) => setFilters(prev => ({ ...prev, anioMin: min, anioMax: max }))} className="py-2" />
                  </div>
                </FilterSection>

                <FilterSection title="Kilometraje" icon={Gauge}>
                  <div className="space-y-4 px-1">
                    <div className="flex justify-between text-xs text-white/70">
                      <span>0 km</span>
                      <span>{filters.kmMax.toLocaleString()} km</span>
                    </div>
                    <Slider value={[filters.kmMax]} min={0} max={200000} step={5000} onValueChange={([val]) => setFilters(prev => ({ ...prev, kmMax: val }))} className="py-2" />
                  </div>
                </FilterSection>

                <FilterSection title="Combustible" icon={Fuel}>
                  <div className="flex flex-wrap gap-2">
                    {combustibles.map(c => (
                      <button key={c} onClick={() => toggleFilter('combustibles', c)} className={`px-3 py-2 rounded-lg text-sm transition-all ${filters.combustibles.includes(c) ? 'bg-green-500 text-white' : 'bg-white/5 text-white/70 hover:bg-white/10'}`}>
                        {c}
                      </button>
                    ))}
                  </div>
                </FilterSection>

                <FilterSection title="Transmisión" icon={Settings2}>
                  <div className="flex gap-2">
                    {transmisiones.map(t => (
                      <button key={t} onClick={() => toggleFilter('transmisiones', t)} className={`flex-1 px-3 py-2 rounded-lg text-sm transition-all ${filters.transmisiones.includes(t) ? 'bg-purple-500 text-white' : 'bg-white/5 text-white/70 hover:bg-white/10'}`}>
                        {t}
                      </button>
                    ))}
                  </div>
                </FilterSection>
              </div>

              <div className="sticky bottom-0 bg-gray-900 border-t border-white/10 p-4 space-y-2">
                <Button onClick={() => setSidebarOpen(false)} className="w-full bg-blue-500 hover:bg-blue-600 text-white">
                  Ver {filteredVehicles.length} resultados
                </Button>
                <Button variant="ghost" onClick={clearFilters} className="w-full text-white/60 hover:text-white">
                  <RotateCcw className="w-4 h-4 mr-2" /> Limpiar filtros
                </Button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Vehiculos;
